/**
 * Cinematic Card - Wide 16:9 aspect ratio with dramatic overlay
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { useEntryText } from './useEntryText';
import { getContentTypeLabel, isVideoContent, getContentTypeColor } from './contentTypeUtils';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-4px) scale(1.01); }
`;

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  isolation: isolate;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .bg-image {
      transform: scale(1.08);
    }

    .overlay {
      background: linear-gradient(
        180deg,
        transparent 20%,
        rgba(0, 0, 0, 0.4) 50%,
        rgba(0, 0, 0, 0.9) 100%
      );
    }

    .content {
      transform: translateY(-8px);
    }

    .play-icon {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const BackgroundImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: -1px;
  background: ${({ image }) => (image ? `url('${image}')` : 'linear-gradient(135deg, #00dda2 0%, #007763 100%)')};
  background-size: cover;
  background-position: center;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 40%,
    rgba(0, 0, 0, 0.3) 60%,
    rgba(0, 0, 0, 0.85) 100%
  );
  transition: background 0.4s ease;
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  color: white;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const TypeBadge = styled.span<{ bgColor?: string }>`
  display: inline-block;
  padding: 4px 12px;
  background: ${({ bgColor }) => bgColor || 'rgba(36, 220, 164, 0.9)'};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Description = styled.p`
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PlayIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    border-left: 20px solid #00dda2;
    margin-left: 4px;
  }
`;

export type CinematicCardProps = {
  entry: ExploreEntry | Recipe;
  linkPrefix?: string;
  showTypeBadge?: boolean;
};

export const CinematicCard: React.FC<CinematicCardProps> = observer(
  ({ entry, linkPrefix = '/academy/', showTypeBadge = true }) => {
    const { title, description, type, imageLarge, imageMedium } = useEntryText(entry);
    const navigate = useNavigate();

    const backgroundImage = imageLarge || imageMedium;

    const handleClick = React.useCallback(() => {
      navigate(`${linkPrefix}${entry.id}`);
    }, [navigate, linkPrefix, entry]);

    const showPlayIcon = isVideoContent(type);

    return (
      <CardContainer onClick={handleClick}>
        <BackgroundImage className="bg-image" image={backgroundImage} />
        <Overlay className="overlay" />
        {showPlayIcon && <PlayIcon className="play-icon" />}
        <Content className="content">
          {showTypeBadge && <TypeBadge bgColor={getContentTypeColor(type)}>{getContentTypeLabel(type)}</TypeBadge>}
          <Title>{title}</Title>
          <Description>{description}</Description>
        </Content>
      </CardContainer>
    );
  },
);
